import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, ArrowDown, ArrowUp, Mic, Plus, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { NavCustomizer } from "@/components/nav/NavCustomizer";
import { useEffectiveNav } from "@/hooks/useEffectiveNav";
import { moveItem } from "@/lib/nav-prefs";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/settings/navigation")({
  component: NavigationSettingsPage,
  head: () => ({
    meta: [
      { title: "Navigation — MissionReady" },
      {
        name: "description",
        content: "Choose which authorized navigation items appear in your bottom bar and sidebar.",
      },
    ],
  }),
});

function NavigationSettingsPage() {
  const nav = useEffectiveNav();

  const selected = nav.bottomItems;

  const persistSidebar = (ids: string[]) => void nav.save({ sidebar_order: ids });

  const moveSidebar = (index: number, delta: number) => {
    persistSidebar(moveItem(nav.sidebarIds, index, index + delta));
  };

  return (
    <div className="mx-auto w-full max-w-2xl space-y-6 px-4 py-6">
      <div className="space-y-1">
        <Link
          to="/settings"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Settings
        </Link>
        <h1 className="text-2xl font-semibold">Navigation</h1>
        <p className="text-sm text-muted-foreground">
          Only features you are currently authorized to use can be selected. If your access changes,
          your navigation updates automatically.
        </p>
      </div>

      {/* Authoritative customize navigation surface */}
      <section className="space-y-3 rounded-xl border border-border bg-card p-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Customize Navigation
        </h2>
        <NavCustomizer />
      </section>

      {/* Live preview */}
      <section className="rounded-xl border border-border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Bottom bar preview · {selected.length}/{nav.maxBottom} visible
        </h2>
        <div
          className="grid overflow-hidden rounded-lg border border-border bg-background"
          style={{
            gridTemplateColumns: `repeat(${Math.max(1, selected.length + (nav.overflow.length ? 1 : 0))}, minmax(0,1fr))`,
          }}
        >
          {selected.map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.id}
                className="flex h-14 flex-col items-center justify-center gap-0.5 text-[10px] text-muted-foreground"
              >
                <Icon className="h-5 w-5" />
                <span className="max-w-full truncate px-0.5">{item.label}</span>
              </div>
            );
          })}
          {nav.overflow.length > 0 && (
            <div className="flex h-14 flex-col items-center justify-center gap-0.5 text-[10px] text-muted-foreground">
              <Plus className="h-5 w-5" />
              <span>More ({nav.overflow.length})</span>
            </div>
          )}
        </div>
      </section>

      {/* DAR placement */}
      {nav.darAuthorized && (
        <section className="space-y-3 rounded-xl border border-border bg-card p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Daily Activity Report
          </h2>
          <p className="flex items-start gap-2 text-xs text-muted-foreground">
            <Mic className="mt-0.5 h-4 w-4 shrink-0" />
            The D.A.R. voice memo microphone is a permanent centre action in the mobile navigation
            bar. It can't be moved or removed, and placement never changes who can record a D.A.R.
          </p>
        </section>
      )}

      {/* Sidebar order */}
      <section className="space-y-3 rounded-xl border border-border bg-card p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Sidebar order
          </h2>
          <Button variant="ghost" size="sm" onClick={() => persistSidebar(nav.defaults.sidebar)}>
            <RotateCcw className="mr-1 h-4 w-4" /> Reset
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Press and hold an item in the side menu to drag it. Sidebar order is independent from the
          bottom bar.
        </p>
        <ul className="space-y-2">
          {nav.sidebarItems.map((item, idx) => {
            const Icon = item.icon;
            return (
              <li
                key={item.id}
                className="flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2"
              >
                <Icon className="h-4 w-4 text-muted-foreground" />
                <span className="flex-1 truncate text-sm">{item.label}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Move ${item.label} up in sidebar`}
                  disabled={idx === 0}
                  onClick={() => moveSidebar(idx, -1)}
                >
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Move ${item.label} down in sidebar`}
                  disabled={idx === nav.sidebarItems.length - 1}
                  onClick={() => moveSidebar(idx, 1)}
                >
                  <ArrowDown className="h-4 w-4" />
                </Button>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}
